import { useMutation } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { AutoComplete, Col, Flex, Form, Row, Slider, Spin } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { ExpensesApis } from 'apis/ExpensesApis';
import { ISearchRouteComboBox } from 'apis/GraphQL/Queries/saleLineQuery';
import {
  IGetAllEmployees,
  IGetSumExpenses,
} from 'apis/GraphQL/Variables/expenses';
import { PlantApi } from 'apis/PlantApis';
import { SaleLineApi } from 'apis/SaleLineApi';
import { FormFilter, SelectForm } from 'components';
import RangePickerForm from 'components/RangePicker/RangePickerForm';
import LazySelectForm, {
  ILazySelectParams,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import { ITable } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import { useEffect, useState } from 'react';
import { initDataTable } from 'utils/constants/constant';
import { convertDateToBE, formatCurrency } from 'utils/constants/functions';
import convertToLabelValueCbb from 'utils/convertToLabelValueCbb';

export interface IEmployee {
  id: string;
  fullName: string;
  employeeId: string;
  label: string;
  value: string;
}

export interface IFilterRouteCostProps {
  defaultFilter: any;
  isOpen: boolean;
  onFinish: (values: IGetSumExpenses) => void;
  onCancel: () => void;
}

export interface IFilterForm {
  plantId: string;
  route: string;
  type: string;
  employeeId: string;
  dateRange: string[];
  costRange: number[];
}

const spanCol = 8;
export const MIN_COST = 0;
export const MAX_COST = 200_000_000;

const FilterRouteCost = ({
  defaultFilter,
  isOpen,
  onCancel,
  onFinish,
}: IFilterRouteCostProps) => {
  const { user } = useUser();
  const [inputValue, setInputValue] = useState<string>('');
  const [form] = useForm<IFilterForm>();
  const [expenseTypes, setExpenseTypes] = useState<DefaultOptionType[]>([]);
  const [plants, setPlants] = useState<DefaultOptionType[]>([]);
  const [routes, setRoutes] = useState<{
    items: any;
    pageInfo: any;
  }>({
    items: [],
    pageInfo: {},
  });
  const inputDebounce = useDebounce(inputValue, 500);
  const [employees, setEmployees] = useState<ITable<IEmployee>>(initDataTable);
  const [employeeId, setEmployeeId] = useState<string>('');

  const { mutate: getAllExpensesType, isPending: getAllExpensesTypeLoading } =
    useMutation({
      mutationFn: () => ExpensesApis.getAllExpensesType(),
      onSuccess: (data) => {
        const types = data.data?.getExpensesType?.items ?? [];

        setExpenseTypes(
          types.map((type: any) => ({
            label: type.id + ' - ' + type.name,
            value: type.id,
          })),
        );
      },
    });

  const { mutate: getAllPlant, isPending: getAllPlantLoading } = useMutation({
    mutationFn: () => PlantApi.getAllPlantComboBox(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants([]);

      setPlants(
        items.map((item: any) => ({
          // label: item.id + ' - ' + item.name,
          label: item.id,
          value: item.id,
        })),
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { isPending: getRouteLoading, mutate: getRouteCb } = useMutation({
    mutationFn: (params: ISearchRouteComboBox) =>
      SaleLineApi.getAllRouteComboBox(params),
    onSuccess: (res) => {
      const items = res.data?.getRouteM?.items || [];

      setRoutes({
        items: convertToLabelValueCbb(items),
        pageInfo: res.data?.getRouteM.pageInfo,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getAllEmployees, isPending: getAllEmployeesLoading } =
    useMutation({
      mutationFn: (params: IGetAllEmployees) =>
        ExpensesApis.getAllEmployees(params),
      onSuccess: (data) => {
        const employees = data.data?.getUser;

        if (!employees) return;

        const items = (employees?.items || []).map((item: any) => ({
          ...item,
          label: item.employeeId
            ? `${item.employeeId} - ${item.fullName}`
            : item.fullName,
          value: item.employeeId
            ? `${item.employeeId} - ${item.fullName}`
            : item.fullName,
        }));

        setEmployees({
          items,
          pageInfo: employees?.pageInfo,
        });
      },
    });

  const handleFinish = (values: IFilterForm) => {
    onFinish({
      fromPrice: values.costRange[0],
      toPrice: values.costRange[1],
      expensesType: values.type,
      plantId: values.plantId,
      routeId: values.route,
      userId:
        inputValue && employeeId && form.getFieldValue('employeeId')
          ? employeeId
          : undefined,
      ...(values.dateRange && {
        fromDate: convertDateToBE(values.dateRange[0]),
        toDate: convertDateToBE(values.dateRange[1]),
      }),
    });
  };

  const handleCancel = () => {
    form.resetFields();

    form.setFieldsValue({
      costRange: [MIN_COST, MAX_COST],
      ...(defaultFilter.plantId && {
        plantId: defaultFilter.plantId,
      }),
    });
    setInputValue('');
    setEmployeeId('');

    onCancel();
  };

  const handleRouteSelectScrollChange = (params: ILazySelectParams) => {
    const plant = form.getFieldValue('plantId');
    getRouteCb({
      value: params.value,
      plantId: plant || defaultFilter.plantId,
      pageSize: params.pageSize,
    });
  };

  const handleClearEmployee = () => {
    setInputValue('');
    setEmployees(initDataTable);
  };

  const handleChangeEmployee = (value: string) => {
    setInputValue(value);
    setEmployeeId('');

    if (!value) setEmployees(initDataTable);
  };

  useEffect(() => {
    getAllExpensesType();
    getAllPlant();
  }, [getAllExpensesType, getAllPlant]);

  useEffect(() => {
    if (inputDebounce) {
      getAllEmployees({
        search: inputDebounce,
      });
    } else {
      setEmployees(initDataTable);
    }
  }, [getAllEmployees, inputDebounce]);

  useEffect(() => {
    if (defaultFilter.plantId) {
      form.setFieldsValue({
        plantId: defaultFilter.plantId,
      });
    }
  }, [defaultFilter.plantId, form]);

  return (
    <Content isOpen={isOpen}>
      <FormFilter form={form} onFinish={handleFinish} onCancel={handleCancel}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                loading={getAllPlantLoading}
                options={plants}
                placeholder="Chọn chi nhánh"
                disabled={user?.plantCode !== '5000'}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Tên tuyến" name="route">
              <LazySelectForm
                customeSize={20}
                data={{
                  options: routes.items || [],
                  totalItems: routes.pageInfo?.totalItems || 0,
                }}
                loading={getRouteLoading}
                placeholder="Chọn tuyến"
                onSelectScrollChange={handleRouteSelectScrollChange}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Loại chi phí" name="type">
              <SelectForm
                loading={getAllExpensesTypeLoading}
                options={expenseTypes}
                placeholder="Chọn loại chi phí"
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Nhân viên" name="employeeId">
              <AutoComplete
                value={inputValue}
                notFoundContent={
                  getAllEmployeesLoading ? (
                    <Flex justify="center">
                      <Spin size="small" />
                    </Flex>
                  ) : null
                }
                allowClear
                size="large"
                className="w-full rounded-xl"
                options={employees.items}
                onClear={handleClearEmployee}
                onChange={handleChangeEmployee}
                onSelect={(value, option) => {
                  setEmployeeId(option.id);
                }}
                placeholder="Tìm kiếm nhân viên"
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Từ ngày - đến ngày" name="dateRange">
              <RangePickerForm
                format={{
                  format: 'DD/MM/YYYY',
                }}
                placeholder={['Từ ngày', 'Đến ngày']}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              initialValue={[MIN_COST, MAX_COST]}
              label="Chọn khoảng chi phí"
              name="costRange"
              required
            >
              <Flex vertical>
                <Slider
                  defaultValue={[MIN_COST, MAX_COST]}
                  rootClassName="!mt-0"
                  range
                  min={MIN_COST}
                  max={MAX_COST}
                  tooltip={{
                    formatter: (value) => formatCurrency({ value: value || 0 }),
                  }}
                  onChange={(value) => {
                    form.setFieldsValue({
                      costRange: value,
                    });
                  }}
                />
                <Flex justify="space-between" align="center">
                  <span>{formatCurrency({ value: MIN_COST })}</span>
                  <span>{formatCurrency({ value: MAX_COST })}</span>
                </Flex>
              </Flex>
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default FilterRouteCost;
